<template>
	<div>
		<!-- regexes table -->
		<table class="table table-bordered my-2">
			<thead>
				<tr class="text-center">
					<th
						scope="col"
						:style="{ width: '30%' }"
						title="Name of the regex"
					>
						Name
					</th>
					<th scope="col" title="Regex">Regex</th>
				</tr>
			</thead>

			<tbody>
				<tr v-for="regex in regexes" :key="regex.id">
					<td :style="{ 'max-width': '0' }">
						<div class="d-flex gap-2 align-items-center">
							<div class="btn-group btn-group-sm">
								<button
									class="btn btn-outline-secondary"
									@click="
										setRegexEnabled(
											regex.id,
											!regex.enabled
										)
									"
									:title="
										regex.enabled ? 'Pause' : 'Continue'
									"
								>
									<i
										class="bi bi-pause-fill"
										v-if="regex.enabled"
									/>
									<i class="bi bi-play-fill" v-else />
								</button>

								<button
									class="btn btn-outline-secondary"
									@click="removeRegex(regex.id)"
									title="Delete"
								>
									<i class="bi bi-trash-fill" />
								</button>
							</div>
							<span class="text-truncate">{{ regex.name }}</span>
						</div>
					</td>
					<td>{{ regex.regex }}</td>
				</tr>

				<tr>
					<td colspan="100">
						<form
							class="d-flex gap-2"
							@submit.prevent="handleAddNewRegex"
						>
							<input
								class="form-control"
								placeholder="Name"
								v-model="newRegex.name"
								type="string"
								:style="{ width: '25%' }"
								required
							/>
							<input
								class="form-control"
								placeholder="Regex"
								v-model="newRegex.regex"
								type="string"
								required
							/>
							<button
								class="btn btn-outline-secondary"
								type="submit"
							>
								Add
							</button>
						</form>
					</td>
				</tr>
			</tbody>
		</table>

		<!-- table settings block -->
		<div class="d-flex my-2 gap-3 align-items-center">
			<ColumnFilterDropdown
				:options="filterOptions"
				:filters="filters"
				class="ms-auto"
				@new-filter="handleAddFilter"
				@delete="deleteFilter"
				@toggle="setFilterEnabled($event.index, $event.enabled)"
			/>
		</div>

		<!-- results table -->
		<table class="table table-bordered">
			<thead class="table-light text-center">
				<tr>
					<th scope="col" title="Sitemap">Sitemap</th>
					<th scope="col" title="Whether HTML matched regex">
						Matched
					</th>
					<th scope="col" title="Regex name">Name</th>
					<th scope="col" :style="{ width: '25%' }" title="URL">
						URL
					</th>
					<th
						scope="col"
						:style="{ width: '45%' }"
						title="List of matches"
					>
						Matches
					</th>
				</tr>
			</thead>
			<tbody>
				<RegexResultRow
					v-for="result in results.slice().reverse()"
					:result="result"
					:key="result.id"
				/>
			</tbody>
		</table>
	</div>
</template>

<script lang="ts">
import {
	ArrayFilter,
	ComparableType,
	restoreFilters,
	saveFilters,
	STRING_COMPARISON_OPERATORS,
	useArrayFilters,
} from "@/hooks/useArrayFilters";
import { regexWatcherService } from "@/services/regexWatcherService";
import { Result } from "@/services/watcherService";
import { computed, defineComponent, ref, watch } from "vue";
import ColumnFilterDropdown, {
	NewArrayFilter,
} from "./ColumnFilterDropdown.vue";
import RegexResultRow from "./RegexResultRow.vue";

export default defineComponent({
	components: { RegexResultRow, ColumnFilterDropdown },
	setup() {
		const {
			regexes,
			regexResults,
			addNewRegex,
			removeRegex,
			setRegexEnabled,
		} = regexWatcherService;

		const emptyRegex = { name: "", regex: "" };
		const newRegex = ref({ ...emptyRegex });
		const handleAddNewRegex = () => {
			addNewRegex(newRegex.value.regex, newRegex.value.name);
			newRegex.value = { ...emptyRegex };
		};

		// result filters
		const filterOptions = [
			{
				value: "matched",
				title: "Matched",
				comparisonOperators: STRING_COMPARISON_OPERATORS,
			},
			{
				value: "name",
				title: "Name",
				comparisonOperators: STRING_COMPARISON_OPERATORS,
			},
		];

		// override filters getters for custom fields
		const {
			filters,
			addFilter,
			deleteFilter,
			applyFilters,
			setFilterEnabled,
		} = useArrayFilters();
		const filterGetterOverrides: Record<string, ArrayFilter["getter"]> = {
			duration: (result: Result) => result.durationMS / 1000,
		};
		const getGetterForKey = (key: string) =>
			filterGetterOverrides[key] ||
			((result: Result) => {
				const value = result[
					key as keyof Result
				] as ComparableType | null;
				return value === null ? "" : value;
			});
		const handleAddFilter = (newFilter: NewArrayFilter) => {
			const getter = getGetterForKey(newFilter.key);
			addFilter({
				...newFilter,
				getter,
				enabled: newFilter.enabled ?? true,
			});
		};

		// save/restore filters from localStorage
		for (const filter of restoreFilters("regexesFilters")) {
			handleAddFilter(filter);
		}
		watch(filters.value, (newFilters: ArrayFilter[]) => {
			saveFilters(newFilters, "regexesFilters");
		});

		// filter results
		const filteredResults = computed(() =>
			applyFilters(regexResults.value, filters.value)
		);

		return {
			regexes,
			results: filteredResults,

			newRegex,
			handleAddNewRegex,
			removeRegex,

			setRegexEnabled,

			filters,
			filterOptions,
			handleAddFilter,
			deleteFilter,
			setFilterEnabled,
		};
	},
});
</script>
